import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useRef, useState} from 'react';

import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FileChooser from 'ui-library/FileChooser';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {upload} from 'util/ImportUtil';
import {formatBytes} from 'util/NameUtil';
import {JSONValue} from 'util/Types';

import {setValueInConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {ConfigShapePath} from '../StepConfigEditorTypes';

interface FileUploadEditorProps {
  config: Record<string, JSONValue>;
  s3UrlPath: ConfigShapePath;
  fileNamePath: ConfigShapePath;
  accept?: string;
  maxFileSizeBytes?: number;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
}

export default function FileUploadEditor({
  config,
  s3UrlPath,
  fileNamePath,
  accept,
  maxFileSizeBytes,
  onChangeConfig,
  disabled,
}: FileUploadEditorProps): JSX.Element {
  const [isUploading, setIsUploading] = useState(false);
  // Always start with null — previously uploaded files have expired S3 URLs and can't be re-opened.
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Refs are needed because the file upload is async: between the time the upload starts and
  // when it completes, the parent may re-render with new config/onChangeConfig props (e.g. the
  // user edits another field). Without refs, the upload callback would close over stale values.
  const configRef = useRef(config);
  configRef.current = config;
  const onChangeConfigRef = useRef(onChangeConfig);
  onChangeConfigRef.current = onChangeConfig;

  const handleSelectFile = useCallback(
    (file: File) => {
      if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
        notifications.error(
          `File ${file.name} exceeds maximum upload size (${formatBytes(maxFileSizeBytes)}).`
        );
        return;
      }
      setIsUploading(true);
      upload(file).then(
        (s3Location: string) => {
          setIsUploading(false);
          setUploadedFileName(file.name);
          // Set both s3URL and fileName in config
          const configWithS3Url = setValueInConfig(configRef.current, s3UrlPath, s3Location);
          onChangeConfigRef.current(configWithS3Url, fileNamePath, file.name);
        },
        (error: {userMessage?: string}) => {
          setIsUploading(false);
          const message = error?.userMessage ?? 'Failed to upload file. Please try again.';
          notifications.error(message);
        }
      );
    },
    [s3UrlPath, fileNamePath, maxFileSizeBytes]
  );

  const handleRemoveFile = useCallback(() => {
    setUploadedFileName(null);
    const configWithClearedS3Url = setValueInConfig(configRef.current, s3UrlPath, null);
    onChangeConfigRef.current(configWithClearedS3Url, fileNamePath, null);
  }, [s3UrlPath, fileNamePath]);

  if (isUploading) {
    return (
      <div className={css(styles.wrapper)}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }

  if (uploadedFileName != null) {
    return (
      <div className={css(styles.wrapper, styles.fileRow)}>
        <BenchlingIcon icon="file" />
        <span className={css(styles.fileName)}>{uploadedFileName}</span>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={handleRemoveFile}
          disabled={disabled}
          text="Remove"
        />
      </div>
    );
  }

  return (
    <div className={css(styles.wrapper)}>
      <FileChooser multiple={false} onSelectFiles={handleSelectFile} accept={accept} />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 8,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  fileName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
